import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useCallback } from 'react';
import {
  ArrowRight,
  Activity,
  Heart,
  Sparkles,
  Camera,
  Brain,
  RotateCcw,
  Award,
  Shield,
  CheckCircle,
  TrendingUp,
  Zap,
  Users,
  Eye,
  ChevronDown,
  Menu,
  X,
  Download
} from 'lucide-react';
import MetricCard from './components/MetricCard';
import { FloatingNav } from './components/FloatingNav';
import CloudBackground from './components/CloudBackground';
import CloudTransition from './components/CloudTransition';
import LongevityBot from './components/LongevityBot';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import Home from './pages/Home';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ContactUs from './pages/ContactUs';
import PricingPage from './pages/PricingPage';
import { supabase } from './lib/supabase';
import { usePWAInstall } from './lib/usePWAInstall';


function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [showSplash, setShowSplash] = useState(true);
  const { isInstallable, isInstalled, handleInstall, isInstalling } = usePWAInstall();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // REPLACE your existing splash screen useEffect with this:
useEffect(() => {
  const minSplashTime = 1000; // Reduced from 2000ms
  const startTime = Date.now();

  const checkCached = async () => {
    if ('caches' in window) {
      try {
        const cache = await caches.match('/');
        if (cache) {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(500, minSplashTime - elapsed);
          setTimeout(() => setShowSplash(false), remaining);
          return;
        }
      } catch (error) {
        console.error('[Cache] Error checking cache:', error);
      }
    }
    setTimeout(() => setShowSplash(false), minSplashTime);
  };

  checkCached();
}, []);

  // ADD this new useEffect for service worker updates:
useEffect(() => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration()
      .then((registration) => {
        if (!registration) return;

        console.log('[SW] Registration found:', registration);

        const updateInterval = setInterval(() => {
          registration.update();
        }, 30 * 60 * 1000);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW] New version available');
              setWaitingWorker(newWorker);
              setShowUpdatePrompt(true);
            }
          });
        });

        return () => clearInterval(updateInterval);
      });

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  }
}, []);

  useEffect(() => {
    const path = window.location.pathname;
    setShowLogin(path === '/login');
    setShowSignUp(path === '/signup');
    setShowTerms(path === '/terms');
    setShowPrivacy(path === '/privacy');
    setShowContact(path === '/contact');
    setShowPricing(path === '/pricing');
  }, []);

  // Handle navigation
  const handleLoginClick = () => {
    window.history.pushState({}, '', '/login');
    setShowLogin(true);
    setShowSignUp(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowContact(false);
    setShowPricing(false);
  };


const handleUpdate = useCallback(() => {
  if (waitingWorker) {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    setShowUpdatePrompt(false);
  }
}, [waitingWorker]);

  const handleSignUpClick = () => {
    window.history.pushState({}, '', '/signup');
    setShowSignUp(true);
    setShowLogin(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowContact(false);
    setShowPricing(false);
  };

  const handleHomeClick = () => {
    window.history.pushState({}, '', '/');
    setShowLogin(false);
    setShowSignUp(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowContact(false);
    setShowPricing(false);
  };

  const handleTermsClick = () => {
    window.history.pushState({}, '', '/terms');
    setShowTerms(true);
    setShowLogin(false);
    setShowSignUp(false);
    setShowPrivacy(false);
    setShowContact(false);
    setShowPricing(false);
  };

  const handlePrivacyClick = () => {
    window.history.pushState({}, '', '/privacy');
    setShowPrivacy(true);
    setShowLogin(false);
    setShowSignUp(false);
    setShowTerms(false);
    setShowContact(false);
    setShowPricing(false);
  };

  const handleContactClick = () => {
    window.history.pushState({}, '', '/contact');
    setShowContact(true);
    setShowLogin(false);
    setShowSignUp(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowPricing(false);
  };

  const handlePricingClick = () => {
    window.history.pushState({}, '', '/pricing');
    setShowPricing(true);
    setShowLogin(false);
    setShowSignUp(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowContact(false);
  };

  // Listen for browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      setShowLogin(path === '/login');
      setShowSignUp(path === '/signup');
      setShowTerms(path === '/terms');
      setShowPrivacy(path === '/privacy');
      setShowContact(path === '/contact');
      setShowPricing(path === '/pricing');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How does AgeQ AI track my biological age?",
      answer: "We analyze multiple biomarkers including inflammation levels, metabolic health, and cellular repair indicators. Our AI combines this data with your daily habits to calculate your biological age versus your chronological age."
    },
    {
      question: "Is the food scanner accurate?",
      answer: "Our computer vision technology has been trained on millions of food images and achieves 95%+ accuracy. It scans not just for calories, but for inflammatory markers and toxicity levels that impact aging."
    },
    {
      question: "What devices do I need?",
      answer: "Just your smartphone. AgeQ AI works on iOS and Android. For enhanced tracking, you can optionally connect wearables like Apple Watch, Oura Ring, or continuous glucose monitors."
    },
    {
      question: "How quickly will I see results?",
      answer: "Most users see measurable improvements in their biological age markers within 30-60 days. The Delta score provides daily feedback, while deeper biomarker changes become evident over 2-3 months."
    },
    {
      question: "What is the Future Face feature?",
      answer: "Using AI prediction models, we project how your face will age based on your current habits and biomarkers. It updates as you improve your health, showing you the tangible impact of your choices."
    },
    {
      question: "Is my health data private and secure?",
      answer: "Absolutely. All data is encrypted end-to-end and stored securely. We never share your personal health information with third parties. You own your data and can export or delete it anytime."
    }
  ];
  const fadeIn = {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const navItems = [
    { name: "Solutions", link: "#solutions" },
    { name: "Pricing", link: "#pricing" },
    { name: "Success Stories", link: "#success" },
    { name: "Resources", link: "#resources" },
  ];

  // Show splash screen with LongevityBot mascot
  if (loading || showSplash) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center -mt-16">
          <LongevityBot size={200} mode="splash" />
        </div>
        <p className="text-sm text-black absolute bottom-1/4">Getting things ready...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Home />;
  }

  if (showLogin) {
    return <Login onSignUpClick={handleSignUpClick} />;
  }

  if (showSignUp) {
    return <SignUp onLoginClick={handleLoginClick} />;
  }

  if (showTerms) {
    return <TermsAndConditions onBack={handleHomeClick} />;
  }

  if (showPrivacy) {
    return <PrivacyPolicy onBack={handleHomeClick} />;
  }

  if (showContact) {
    return <ContactUs onBack={handleHomeClick} />;
  }

  if (showPricing) {
    return <PricingPage onBack={handleHomeClick} onGetStarted={handleLoginClick} />;
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 selection:bg-cyan-100">

      <AnimatePresence>
      {showUpdatePrompt && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-3 shadow-lg"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="font-semibold text-sm">New version available!</p>
                <p className="text-xs text-white/90">Update now for the latest features</p>
              </div>
            </div>
            <button
              onClick={handleUpdate}
              className="px-4 py-2 bg-white text-cyan-600 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap flex-shrink-0"
            >
              Update Now
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>


     {/* Floating Navigation */}
<FloatingNav navItems={navItems} onLoginClick={handleLoginClick} />

{/* Static Navigation - Transparent */}
<nav className="absolute top-0 left-0 right-0 z-40 px-4 sm:px-8 py-4">
  <div className="max-w-7xl mx-auto flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
        <Activity className="w-6 h-6 text-white" strokeWidth={2.5} />
      </div>
      <h1 className="text-xl font-bold tracking-tight text-white">
        AgeQ AI
      </h1>
    </div>

    <div className="hidden md:flex items-center gap-8 text-sm font-medium">
      <a href="#solutions" className="text-white/90 hover:text-white transition-colors">Solutions</a>
      <a href="#pricing" className="text-white/90 hover:text-white transition-colors">Pricing</a>
      <a href="#success" className="text-white/90 hover:text-white transition-colors">Success Stories</a>
      <a href="#resources" className="text-white/90 hover:text-white transition-colors">Resources</a>
    </div>

    <div className="flex items-center gap-3">
      <button 
        onClick={handleLoginClick}
        className="hidden md:inline-flex px-5 py-2 font-medium text-white/90 hover:text-white transition-colors"
      >
        Log in
      </button>
      <button 
        onClick={handleLoginClick}
        className="hidden md:inline-flex px-6 py-2.5 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition-colors shadow-md">
        Try it now
      </button>
      <button
        className="md:hidden p-2 text-white hover:text-white/80"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
    </div>
  </div>

</nav>

{/* Full Screen Mobile Menu */}
<AnimatePresence>
  {mobileMenuOpen && (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed inset-0 z-50 bg-white md:hidden"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 px-6 py-4 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900">
            AgeQ AI
          </h1>
        </div>
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Menu Content */}
      <div className="flex flex-col h-full px-6 pt-24 pb-8">
        {/* Navigation Links */}
        <nav className="flex-1 space-y-1">
          <a
            href="#solutions"
            className="flex items-center justify-between py-4 px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 text-lg font-medium rounded-xl transition-all group"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span>Solutions</span>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all" />
          </a>
          <a
            href="#pricing"
            className="flex items-center justify-between py-4 px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 text-lg font-medium rounded-xl transition-all group"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span>Pricing</span>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all" />
          </a>
          <a
            href="#success"
            className="flex items-center justify-between py-4 px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 text-lg font-medium rounded-xl transition-all group"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span>Success Stories</span>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all" />
          </a>
          <a
            href="#resources"
            className="flex items-center justify-between py-4 px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 text-lg font-medium rounded-xl transition-all group"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span>Resources</span>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all" />
          </a>
        </nav>

        {/* Bottom Actions */}
        <div className="space-y-3 pt-6 border-t border-gray-100">
          <button
            onClick={handleLoginClick}
            className="block w-full py-4 text-gray-700 hover:text-gray-900 text-lg transition-colors text-center font-medium hover:bg-gray-50 rounded-xl"
          >
            Log in
          </button>
          <button 
            onClick={handleLoginClick}
            className="block w-full py-4 px-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl text-center">
            Try it now
          </button>
        </div>
      </div>
    </motion.div>
  )}
</AnimatePresence>

      <main>
        {/* HERO SECTION */}
        <section className="relative px-4 sm:px-8 pt-24 sm:pt-20 pb-8 sm:pb-12 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="/rewindbg.webp" 
              alt="" 
              className="w-full h-full object-cover object-bottom"
            />
          </div>
          
          {/* Gradient Overlay - Fades mountains into white */}
          <div className="absolute inset-0 -bottom-20" 
               style={{
                 background: 'linear-gradient(to bottom, transparent 0%, transparent 60%, white 85%, white 100%)'
               }}
          ></div>
          
          {/* Additional blur overlay at bottom for desktop only */}
          <div className="hidden md:block absolute bottom-0 left-0 right-0 h-48 backdrop-blur-md bg-gradient-to-b from-transparent to-white/60 pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">

              {/* Left Content */}
              <motion.div
                {...fadeIn}
                transition={{ duration: 0.8 }}
                className="order-1 lg:order-1"
              >
                {/* Rating Badge */}
                <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 bg-white/90 backdrop-blur-sm border border-white/20 rounded-full mb-6 sm:mb-8 shadow-lg">
                  <div className="flex -space-x-1.5 sm:-space-x-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 border-2 border-white"></div>
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 border-2 border-white"></div>
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 border-2 border-white"></div>
                  </div>
                  <span className="text-xs sm:text-sm text-gray-800 font-medium">
                    Loved by 5M users with <span className="inline-flex items-center gap-1">⭐ <span className="font-semibold">4.9</span></span> rating
                  </span>
                </div>

                {/* Main Headline - ENHANCED VERSION */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-3 sm:mb-4 leading-[1.1] tracking-tight text-white [text-shadow:_0_2px_20px_rgb(0_0_0_/_60%),_0_4px_8px_rgb(0_0_0_/_40%)]">
                  Don't just track your age
                </h1>
                <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-3 sm:mb-4 leading-[1.1] tracking-tight text-white [text-shadow:_0_2px_20px_rgb(0_0_0_/_60%),_0_4px_8px_rgb(0_0_0_/_40%)]">
                  Reverse it.
                </h2>

                {/* Description - Enhanced */}
                <p className="text-base sm:text-lg md:text-xl text-white/95 mb-6 sm:mb-10 leading-relaxed max-w-xl font-medium drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                  Your birth date is fixed. Your body isn't. The first AI that helps you get younger.
                </p>

                {/* Download Button */}
                <div className="flex flex-wrap gap-4 mb-8 lg:mb-0">
                  <button 
                    onClick={handleLoginClick}
                    className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-black text-white font-bold tracking-wide rounded-xl hover:bg-zinc-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-sm sm:text-base w-full sm:w-auto">
                    <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.5} />
                    START REVERSING
                  </button>
                </div>
              </motion.div>

              {/* Right - Phone Mockups */}
              <motion.div
                className="relative h-[400px] sm:h-[500px] lg:h-[700px] order-2 lg:order-2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Phone 1 - Scanner View */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[160px] sm:w-[200px] md:w-[280px] lg:w-[320px] z-10 transform -rotate-6">
                  <div className="relative bg-gray-900 rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] p-1.5 sm:p-2 md:p-3 shadow-2xl">
                    <div className="bg-white rounded-[1.25rem] sm:rounded-[1.75rem] md:rounded-[2.5rem] overflow-hidden">
                      {/* Status Bar */}
                      <div className="bg-gray-900 text-white px-2 sm:px-3 md:px-6 py-1 sm:py-1.5 md:py-2 flex justify-between items-center text-[10px] sm:text-xs">
                        <span className="font-semibold">9:41</span>
                        <div className="flex items-center gap-1">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>
                        </div>
                      </div>

                      {/* Scanner Screen */}
                      <div className="relative h-[280px] sm:h-[350px] md:h-[450px] lg:h-[500px] bg-gradient-to-br from-gray-100 to-gray-50">
                        <div className="absolute top-2 sm:top-3 md:top-4 left-0 right-0 text-center">
                          <div className="inline-flex items-center gap-2 text-white font-semibold text-[10px] sm:text-xs md:text-sm">
                            <span>Scanner</span>
                          </div>
                        </div>

                        {/* Food Image Placeholder */}
                        <div className="absolute inset-3 sm:inset-4 md:inset-8 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center">
                          <div className="text-3xl sm:text-4xl md:text-6xl">🥞</div>
                        </div>

                        {/* Scan Frame */}
                        <div className="absolute inset-3 sm:inset-4 md:inset-8 border-2 md:border-4 border-white rounded-lg sm:rounded-xl md:rounded-2xl"></div>

                        {/* Bottom Button */}
                        <div className="absolute bottom-3 sm:bottom-4 md:bottom-8 left-0 right-0 flex justify-center">
                          <div className="px-2 py-1.5 sm:px-3 sm:py-2 md:px-6 md:py-3 bg-white rounded-full shadow-lg flex items-center gap-1 sm:gap-1.5 md:gap-2 text-[10px] sm:text-xs md:text-sm">
                            <Camera className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-5 md:h-5" />
                            <span className="font-semibold">Scan food</span>
                          </div>
                        </div>

                        {/* Camera Button */}
                        <div className="absolute bottom-8 sm:bottom-10 md:bottom-20 left-1/2 -translate-x-1/2">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-16 md:h-16 bg-white rounded-full border-2 md:border-4 border-gray-300"></div>
                        </div>
                      </div>
                    </div>
                    {/* Phone notch */}
                    <div className="absolute top-1.5 sm:top-2 md:top-3 left-1/2 -translate-x-1/2 w-16 h-3 sm:w-20 sm:h-4 md:w-32 md:h-6 bg-gray-900 rounded-b-lg sm:rounded-b-xl md:rounded-b-2xl"></div>
                  </div>
                </div>

                {/* Phone 2 - Results View */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[160px] sm:w-[200px] md:w-[280px] lg:w-[320px] z-20 transform rotate-6">
                  <div className="relative bg-gray-900 rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[3rem] p-1.5 sm:p-2 md:p-3 shadow-2xl">
                    <div className="bg-white rounded-[1.25rem] sm:rounded-[1.75rem] md:rounded-[2.5rem] overflow-hidden">
                      {/* Status Bar */}
                      <div className="bg-gray-900 text-white px-2 sm:px-3 md:px-6 py-1 sm:py-1.5 md:py-2 flex justify-between items-center text-[10px] sm:text-xs">
                        <span className="font-semibold">9:41</span>
                        <div className="flex items-center gap-1">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>
                        </div>
                      </div>

                      {/* Results Screen */}
                      <div className="bg-white p-2 sm:p-3 md:p-6 h-[280px] sm:h-[350px] md:h-[450px] lg:h-[500px] overflow-hidden">
                        <h3 className="text-sm sm:text-base md:text-xl font-bold mb-1.5 sm:mb-2 md:mb-4">Nutrition</h3>

                        {/* Food Items */}
                        <div className="space-y-1.5 sm:space-y-2 md:space-y-3 mb-2 sm:mb-3 md:mb-6">
                          <div className="bg-gray-50 rounded-lg md:rounded-xl p-1.5 sm:p-2 md:p-3 flex items-center gap-1.5 sm:gap-2 md:gap-3">
                            <div className="text-base sm:text-lg md:text-2xl">🫐</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[10px] sm:text-xs md:text-base truncate">Blueberries</div>
                              <div className="text-[9px] sm:text-xs md:text-sm text-gray-500">8</div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg md:rounded-xl p-1.5 sm:p-2 md:p-3 flex items-center gap-1.5 sm:gap-2 md:gap-3">
                            <div className="text-base sm:text-lg md:text-2xl">🥞</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[10px] sm:text-xs md:text-base truncate">Pancakes</div>
                              <div className="text-[9px] sm:text-xs md:text-sm text-gray-500">695</div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg md:rounded-xl p-1.5 sm:p-2 md:p-3 flex items-center gap-1.5 sm:gap-2 md:gap-3">
                            <div className="text-base sm:text-lg md:text-2xl">🍯</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[10px] sm:text-xs md:text-base truncate">Syrup</div>
                              <div className="text-[9px] sm:text-xs md:text-sm text-gray-500">12</div>
                            </div>
                          </div>
                        </div>

                        <div className="text-[9px] sm:text-xs md:text-sm font-semibold mb-0.5 sm:mb-1 md:mb-2">Breakfast</div>
                        <div className="text-xs sm:text-sm md:text-lg font-bold mb-1.5 sm:mb-2 md:mb-4 line-clamp-1">Pancakes with blueberries & syrup</div>

                        {/* Macros Grid */}
                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-3 mb-2 sm:mb-3 md:mb-4">
                          <div className="flex items-center gap-1 md:gap-2">
                            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                            <div className="min-w-0">
                              <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-500">Calories</div>
                              <div className="font-bold text-[10px] sm:text-xs md:text-base">616</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 md:gap-2">
                            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
                            <div className="min-w-0">
                              <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-500">Carbs</div>
                              <div className="font-bold text-[10px] sm:text-xs md:text-base">93g</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 md:gap-2">
                            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 bg-pink-500 rounded-full flex-shrink-0"></div>
                            <div className="min-w-0">
                              <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-500">Protein</div>
                              <div className="font-bold text-[10px] sm:text-xs md:text-base">11g</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 md:gap-2">
                            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                            <div className="min-w-0">
                              <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-500">Fats</div>
                              <div className="font-bold text-[10px] sm:text-xs md:text-base">21g</div>
                            </div>
                          </div>
                        </div>

                        {/* Health Score */}
                        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 mb-2 sm:mb-3 md:mb-4">
                          <Heart className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-pink-500 flex-shrink-0" />
                          <span className="text-[9px] sm:text-xs md:text-sm font-semibold whitespace-nowrap">Health score</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-1 sm:h-1.5 md:h-2">
                            <div className="bg-green-500 h-1 sm:h-1.5 md:h-2 rounded-full" style={{width: '70%'}}></div>
                          </div>
                          <span className="text-[9px] sm:text-xs md:text-sm font-bold whitespace-nowrap">7/10</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-1 sm:gap-1.5 md:gap-2">
                          <button className="flex-1 py-1 sm:py-1.5 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl font-semibold text-[10px] sm:text-xs md:text-sm hover:bg-gray-50">
                            ⚡ Fix
                          </button>
                          <button className="flex-1 py-1 sm:py-1.5 md:py-2.5 bg-black text-white rounded-lg md:rounded-xl font-semibold text-[10px] sm:text-xs md:text-sm hover:bg-gray-800">
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Phone notch */}
                    <div className="absolute top-1.5 sm:top-2 md:top-3 left-1/2 -translate-x-1/2 w-16 h-3 sm:w-20 sm:h-4 md:w-32 md:h-6 bg-gray-900 rounded-b-lg sm:rounded-b-xl md:rounded-b-2xl"></div>
                  </div>
                </div>

                {/* Decorative Arrow - hidden on mobile */}
                <div className="hidden lg:block absolute left-[45%] top-[35%] z-30">
                  <svg width="120" height="80" viewBox="0 0 120 80" fill="none" className="text-gray-900">
                    <path d="M10 40 Q 40 10, 80 40" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="4 4"/>
                  </svg>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* PROBLEM SECTION */}
        <div className="relative -mt-20 sm:-mt-24 md:-mt-28">
          <CloudBackground className="!min-h-0 py-24 sm:py-32 md:py-40 pb-32 sm:pb-40 md:pb-48">
            <div className="px-4 sm:px-6 md:px-8 max-w-6xl mx-auto">
              <motion.h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4 sm:mb-6 tracking-tight text-white leading-tight px-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Tracking calories is easy.
                <br />
                <span className="text-white">Optimizing longevity? Frustrating</span>
              </motion.h2>

              <motion.div
                className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-12 sm:mt-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="text-center px-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-cyan-100">
                    <Activity className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-600" strokeWidth={2} />
                  </div>
                  <p className="text-white leading-relaxed text-sm sm:text-base drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)] font-medium">
                    Most trackers measure output (steps) but ignore the internal inflammation that silently accelerates your aging process.
                  </p>
                </div>

                <div className="text-center px-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-cyan-100">
                    <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-600" strokeWidth={2} />
                  </div>
                  <p className="text-white leading-relaxed text-sm sm:text-base drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)] font-medium">
                   You lose years of potential healthspan by following generic 'wellness' advice that ignores your unique biological triggers.
                  </p>
                </div>

                <div className="text-center px-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-cyan-100">
                    <Zap className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-600" strokeWidth={2} />
                  </div>
                  <p className="text-white leading-relaxed text-sm sm:text-base drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)] font-medium">
                    The feedback loop is broken. You often don't know if your expensive supplements or diets are working until it's too late.
                  </p>
                </div>
              </motion.div>
            </div>
          </CloudBackground>
          {/* Smooth gradient blend transition from cloud blue to black */}
          <div className="absolute bottom-0 left-0 right-0 h-32 sm:h-40 md:h-48 bg-gradient-to-b from-transparent via-cyan-900/20 to-black pointer-events-none"></div>
        </div>

        {/* SOLUTION SECTION */}
        <section className="px-8 py-24 bg-black text-white relative overflow-hidden">
          {/* Decorative stars */}
          <div className="absolute top-24 left-16 text-white opacity-30">✦</div>
          <div className="absolute top-40 right-32 text-white opacity-20">✦</div>
          <div className="absolute bottom-32 left-8 text-white opacity-20">✦</div>
          <div className="absolute bottom-64 right-16 text-white opacity-30">✦</div>
          <div className="absolute top-1/2 right-1/4 text-white opacity-10">✦</div>
          <div className="absolute top-1/3 left-1/4 text-white opacity-10">✦</div>

          <div className="max-w-7xl mx-auto relative z-10">

            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight leading-tight">
                AI that audits your biology &
                <br />
                <span className="text-white">actually helps you reverse your age.</span>
              </h2>
            </motion.div>

            {/* Role Tabs */}
            <motion.div
              className="flex flex-wrap justify-center gap-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <button className="px-6 py-3 bg-gray-700/80 hover:bg-gray-600 rounded-xl text-sm font-medium transition-colors">
                Inflammation Audit
              </button>
              <button className="px-6 py-3 bg-gray-700/80 hover:bg-gray-600 rounded-xl text-sm font-medium transition-colors">
               Metabolic Score
              </button>
              <button className="px-6 py-3 bg-gray-700/80 hover:bg-gray-600 rounded-xl text-sm font-medium transition-colors">
                Cellular Repair
              </button>
            </motion.div>

            {/* Main Content Description */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-base md:text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Master your inputs and optimize recovery. We analyze thousands of data points to help you evolve your wellness.
              </p>
            </motion.div>

            {/* Flow Diagram */}
            <motion.div
              className="relative mb-20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="max-w-6xl mx-auto gap-4 grid grid-cols-12 grid-rows-2">
                {/* Card 1: Computer Vision */}
                <div className="col-span-12 sm:col-span-4 h-[260px] rounded-2xl backdrop-blur-sm bg-gradient-to-br from-cyan-900/20 via-blue-900/30 to-blue-950/40 border border-gray-700/30 p-6 flex flex-col">
                  <p className="text-xs text-cyan-400 uppercase font-bold tracking-wider mb-3">AI Analysis</p>
                  <h4 className="text-white font-semibold text-2xl mb-3">Computer Vision.</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Scanning for toxicity, not just calories.
                  </p>
                </div>

                {/* Card 2: Future Face */}
                <div className="col-span-12 sm:col-span-4 h-[260px] rounded-2xl backdrop-blur-sm bg-gradient-to-br from-purple-900/20 via-pink-900/30 to-purple-950/40 border border-gray-700/30 p-6 flex flex-col">
                  <p className="text-xs text-cyan-400 uppercase font-bold tracking-wider mb-3">Prediction</p>
                  <h4 className="text-white font-semibold text-2xl mb-3">Future Face.</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    See your face in 2040 based on today.
                  </p>
                </div>

                {/* Card 3: Blind Spots */}
                <div className="col-span-12 sm:col-span-4 h-[260px] rounded-2xl backdrop-blur-sm bg-gradient-to-br from-teal-900/20 via-emerald-900/30 to-teal-950/40 border border-gray-700/30 p-6 flex flex-col">
                  <p className="text-xs text-cyan-400 uppercase font-bold tracking-wider mb-3">Discovery</p>
                  <h4 className="text-white font-semibold text-2xl mb-3">Blind Spots.</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Find 'healthy' habits that are aging you.
                  </p>
                </div>

                {/* Card 4: The Delta */}
                <div className="col-span-12 sm:col-span-6 h-[260px] rounded-2xl backdrop-blur-sm bg-gradient-to-br from-blue-900/20 via-indigo-900/30 to-blue-950/40 border border-gray-700/30 p-6 flex flex-col">
                  <p className="text-xs text-cyan-400 uppercase font-bold tracking-wider mb-3">Daily Score</p>
                  <h4 className="text-white font-semibold text-2xl mb-3">The Delta.</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    One daily score. Positive or Negative.
                  </p>
                </div>

                {/* Card 5: Live Tracking */}
                <div className="col-span-12 sm:col-span-6 h-[260px] rounded-2xl backdrop-blur-sm bg-gradient-to-br from-orange-900/20 via-red-900/30 to-orange-950/40 border border-gray-700/30 p-6 flex flex-col">
                  <p className="text-xs text-cyan-400 uppercase font-bold tracking-wider mb-3">Real-time</p>
                  <h4 className="text-white font-semibold text-2xl mb-3">Live Tracking.</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Monitor your biomarkers in real-time.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Stats Section */}
            <motion.div
              className="mt-20 pt-16 border-t border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h3 className="text-3xl font-bold mb-12 text-center">Best in class</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-cyan-400 mb-3">6.9%</div>
                  <p className="text-gray-400 text-sm uppercase tracking-wider">Average Bio-Age Reduction</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-cyan-400 mb-3">50%</div>
                  <p className="text-gray-400 text-sm uppercase tracking-wider">Faster Results</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-cyan-400 mb-3">30%</div>
                  <p className="text-gray-400 text-sm uppercase tracking-wider">Improved Adherence</p>
                </div>
              </div>
            </motion.div>
          </div>
          {/* Gradient transition from dark to white */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-white pointer-events-none"></div>
        </section>

        {/* METRICS SECTION */}
        <section className="px-8 py-24 bg-white">
          <div className="max-w-7xl mx-auto">
            <motion.h2
              className="text-4xl font-bold text-center mb-4 tracking-tight text-gray-900"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Track What Matters Most
            </motion.h2>
            <motion.p
              className="text-center text-gray-600 mb-16 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Monitor key biomarkers and health metrics with precision tracking and AI-powered insights.
            </motion.p>
            <motion.div
              className="grid md:grid-cols-3 gap-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <MetricCard
                icon={Activity}
                title="Inflammation"
                description="Track systemic inflammation markers to identify cellular stress."
              />
              <MetricCard
                icon={Heart}
                title="Metabolic Health"
                description="Monitor glucose regulation and insulin sensitivity."
              />
              <MetricCard
                icon={Sparkles}
                title="Cellular Repair"
                description="Measure autophagy and mitochondrial function."
              />
            </motion.div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="relative px-8 py-24 bg-white">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight text-gray-900">
                FAQs
              </h2>
              <p className="text-gray-600 text-lg">
                Everything you need to know about reversing your age
              </p>
            </motion.div>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-8">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {expandedFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-0">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CLOUD TRANSITION */}
        <CloudTransition />

        {/* FINAL CTA */}
        <section className="relative">
          <div className="px-8 py-32 bg-gradient-to-b from-gray-900 via-blue-950 to-black text-white relative overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight leading-tight">
                    Ready to optimize your health?
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={handleLoginClick}
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                      Start Now
                      <ArrowRight className="w-5 h-5" />
                    </button>
                 
                  </div>
                </motion.div>

                <motion.div
                  className="relative hidden lg:block"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 blur-3xl"></div>
                    <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
                      <div className="text-center mb-6">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500"></div>
                        <h3 className="text-xl font-bold mb-1">Your Health Journey</h3>
                        <p className="text-gray-400 text-sm">Track your progress</p>
                      </div>
                      <div className="flex gap-2 mb-4">
                        <span className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium border border-cyan-500/30">
                          Metrics
                        </span>
                        <span className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium border border-purple-500/30">
                          Insights
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm mb-6">
                        Get personalized recommendations to optimize your health and reverse aging.
                      </p>
                      <button 
                        onClick={handleLoginClick}
                        className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-all"
                      >
                        Start tracking
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-black text-white px-8 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="flex flex-col items-center text-center space-y-8">
            {/* PWA Download Button */}
            {isInstallable && !isInstalled && (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                onClick={handleInstall}
                disabled={isInstalling}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInstalling ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Installing...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download App</span>
                  </>
                )}
              </motion.button>
            )}

            {isInstalled && (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/20 text-green-400 border border-green-500/30 rounded-xl">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">App Installed</span>
              </div>
            )}

            {/* Footer Links */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-gray-400 text-sm">
              <button onClick={handleTermsClick} className="hover:text-white transition-colors">
                Terms and Conditions
              </button>
              <button onClick={handlePrivacyClick} className="hover:text-white transition-colors">
                Privacy Policy
              </button>
              <button onClick={handleContactClick} className="hover:text-white transition-colors">
                Contact Us
              </button>
              <button onClick={handlePricingClick} className="hover:text-white transition-colors">
                Pricing
              </button>
            </div>

            {/* Copyright */}
            <p className="text-gray-500 text-sm">
              &copy; 2025 AgeQ AI . All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  ); 
} 
 
export default App;
